import React, { useEffect, useRef, useState } from "react";
import { getCookie, setCookie, removeCookie } from "src/utils/cookies";
import { RCCHookToast, RCCServicesApi } from "react-common-components";
import hasPermissions, { hasContractPermissions, hasSpecificPermissions } from "src/utils/hasPermissions";
import { getLocalStorage, removeLocalStorage, setLocalStorage } from "src/utils/localStorage";
import { CookieAttributes } from "js-cookie";
import { getSessionStorage, removeSessionStorage, setSessionStorage } from "src/utils/sessionStorage";
import ModalTermsUse from "src/components/ModalTermsUse";
import IRefModalTermsUse from "src/components/ModalTermsUse/interface";
import ModalsProfile from "src/pages/Profile/modals";
import { IRefModalsProfile } from "src/pages/Profile/interface";
import ScreenStandardGroup from "src/Interfaces/screenStandardGroup";
import { AuthContext } from "./context";
import { ICompany, ICredentials, IBusinessGroup, IPermissions, IRefModalTFA, IUserConfig, PermissionEnum, ISystemNotifications } from "./interface";
import ModalTFA from "./modalTFA";
import { useTranslation } from "../Translations/context";
import { useReloadAnimation } from "../ReloadAnimation/context";
import { useMaintenance } from "../Maintenance/context";

export const signOutFunction = () => {
	const rememberMe = getCookie("RememberMe") === "true";
	const removeCookieFunction = (name: string) => rememberMe ? removeCookie(name) : removeSessionStorage(name);

	RCCServicesApi.deleteRequest({ method: "User/DeleteSession" });
	removeCookieFunction("Token");
	removeCookieFunction("User");
	removeCookieFunction("BusinessGroup");
	removeCookieFunction("Company");
	removeLocalStorage("PermissionsMenu");
	removeCookieFunction("Administrator");
	removeCookieFunction("SuperUser");
	removeCookieFunction("HomePage");
	removeCookieFunction("ScheduleRestriction");

	removeCookieFunction(ScreenStandardGroup.ACTION);
	removeCookieFunction(ScreenStandardGroup.AUDITS);
	removeCookieFunction(ScreenStandardGroup.INDICATORS);
	removeCookieFunction(ScreenStandardGroup.MEETINGS);
	removeCookieFunction(ScreenStandardGroup.OCCURRENCES);		
	removeCookieFunction(ScreenStandardGroup.OKR);			
	removeCookieFunction(ScreenStandardGroup.RISKS);
	removeCookieFunction("AppDefaultDashboard");
	removeCookieFunction("NeedToChangePassword");
	
	window.location.reload();
};

const AuthProvider: React.FC = ({children}) => {
	let rememberMe = getCookie("RememberMe") === "true";
	const getCookieFunction = (name: string) => rememberMe ? getCookie(name) : getSessionStorage(name);
	const setCookieFunction = (name: string, content: string, options?: CookieAttributes | undefined) => rememberMe ? setCookie(name, content, options) : setSessionStorage(name, content);
	const removeCookieFunction = (name: string) => rememberMe ? removeCookie(name) : removeSessionStorage(name);

	const getCredentials = () => {
		const token = getCookieFunction("Token");
		const eftba005 = getCookieFunction("User");  
		const eftba001 = getCookieFunction("BusinessGroup");
		const eftba002 = getCookieFunction("Company");	
		const administrator = getCookieFunction("Administrator") === "true";
		const superUser = getCookieFunction("SuperUser") === "true";

		if(token && eftba005 && eftba001 && eftba002){
			RCCServicesApi.addHeader("authorization", `Bearer ${token}`);
			return { 
				token, 
				eftba005: JSON.parse(eftba005),
				eftba001: JSON.parse(eftba001),
				eftba002: JSON.parse(eftba002),
				administrator,
				superUser 
			};
		}

		return {} as ICredentials;
	};

	const getPermissions = () => {
		const eftbb001 = getLocalStorage("PermissionsMenu");

		if(eftbb001){
			return JSON.parse(eftbb001);
		}

		return [] as IPermissions[];
	};

	const modalTFA = useRef<IRefModalTFA>(null);
	const modalTermsUseRef = useRef<IRefModalTermsUse>(null);
	const modalsProfileRef = useRef<IRefModalsProfile>(null);
	
	const { translate, changeLanguage } = useTranslation();
	const { addToast } = RCCHookToast();
	const { hotReload } = useReloadAnimation();
	const { updateMaintenance } = useMaintenance();
	const [credentials, setCredentials] = useState<ICredentials>(getCredentials());
	const [permissions, setPermissions] = useState<IPermissions[]>(getPermissions());
	const [defaultDashboard, setDefaultDashboard] = useState<string | null>(getCookieFunction("AppDefaultDashboard") || null);
	const [homePage, setHomePage] = useState<string>(getCookieFunction("HomePage") || "1");
	const [scheduleRestrict, setScheduleRestrict] = useState(getCookieFunction("ScheduleRestriction") === "true");
	const [notifications, setSystemNotifications] = useState<ISystemNotifications[]>([]);
	const updateUserConfig = (userConfig: IUserConfig) => {
		let dashboardId = null;
		if(userConfig !== null){
			if(userConfig.efa105Painelacoes)
				setCookieFunction(ScreenStandardGroup.ACTION, userConfig.efa105Painelacoes);
			if(userConfig.efa105Painelauditorias)
				setCookieFunction(ScreenStandardGroup.AUDITS, userConfig.efa105Painelauditorias);
			if(userConfig.efa105Painelindicadores)
				setCookieFunction(ScreenStandardGroup.INDICATORS, userConfig.efa105Painelindicadores);
			if(userConfig.efa105Painelocorrencias)
				setCookieFunction(ScreenStandardGroup.OCCURRENCES, userConfig.efa105Painelocorrencias);
			if(userConfig.efa105Painelokr)
				setCookieFunction(ScreenStandardGroup.OKR, userConfig.efa105Painelokr);	
			if(userConfig.efa105Painelriscos)
				setCookieFunction(ScreenStandardGroup.RISKS, userConfig.efa105Painelriscos);
			if(userConfig.efa105Dashboard)
				setCookieFunction("AppDefaultDashboard", userConfig.efa105Dashboard);
			if(userConfig.efa105Painelreunioes)
				setCookieFunction(ScreenStandardGroup.MEETINGS, userConfig.efa105Painelreunioes);
	
			dashboardId = userConfig.efa105Dashboard !== undefined && userConfig.efa105Dashboard !== null ? String(userConfig.efa105Dashboard) : null;
		}
	
		setDefaultDashboard(dashboardId);
	};
	const proceedToSucessfulLogin = ({ token, eftba005, eftbb001,  eftba001, eftba002, administrator, superUser, userConfig, scheduleRestriction, systemNotifications, tokenExpirationHours, needToChangePassword } : any) => {
		updateUserConfig(userConfig);
		setHomePage(eftba005.efa005Paginicial.toString());

		setSystemNotifications(systemNotifications);
		changeLanguage(eftba005.efa005Idioma ?? "pt");

		const expires = tokenExpirationHours / 24;

		setCookieFunction("HomePage", eftba005.efa005Paginicial.toString(), { secure: true, expires });
		setCookieFunction("Token", token, { secure: true, expires });
		setCookieFunction("User", JSON.stringify(eftba005), { secure: true, expires });
		setCookieFunction("BusinessGroup", JSON.stringify(eftba001), { secure: true, expires });
		setCookieFunction("Company", JSON.stringify(eftba002), { secure: true, expires });
		setLocalStorage("PermissionsMenu", JSON.stringify(eftbb001));
		setCookieFunction("Administrator", String(administrator), { secure: true, expires });
		setCookieFunction("SuperUser", String(superUser), { secure: true, expires });
		setCookieFunction("ScheduleRestriction", String(scheduleRestriction),  { secure: true, expires });
		setScheduleRestrict(String(scheduleRestriction) === "true");

		RCCServicesApi.addHeader("authorization", `Bearer ${token}`);

		setCredentials({token, eftba005, eftba001, eftba002, administrator, superUser});
		setPermissions(eftbb001);

		hotReload();

		if(needToChangePassword)
			setCookieFunction("NeedToChangePassword", "true");

		window.setTimeout(() => {

			addToast({
				title: translate("Sucesso"),
				description: translate("Autenticado com sucesso"),
				color: "green"
			});

			if(needToChangePassword)
				modalsProfileRef.current?.openModalChangePassword(true);
			
		} , 2600);
	};
	const executeOnSuccessfulLogin = ({ token, eftba005, eftbb001, eftba001, eftba002, administrator, superUser, userConfig, scheduleRestriction, systemNotifications, hasUseTermNotAccepted, tokenExpirationHours, needToChangePassword, executeAfterTermsUseAccepted } : any) => {
		if(hasUseTermNotAccepted){
			RCCServicesApi.addHeader("authorization", `Bearer ${token}`);
			modalTermsUseRef.current?.openModalAcceptTermsUse(eftba005.efa005Id, () => {
				if (executeAfterTermsUseAccepted)
					executeAfterTermsUseAccepted();
				proceedToSucessfulLogin({ token, eftba005, eftbb001,  eftba001, eftba002, administrator, superUser, userConfig, scheduleRestriction, systemNotifications, tokenExpirationHours, needToChangePassword });
			});
			return;
		}
		proceedToSucessfulLogin({ token, eftba005, eftbb001,  eftba001, eftba002, administrator, superUser, userConfig, scheduleRestriction, systemNotifications, tokenExpirationHours, needToChangePassword });
	};
	const signIn = async({email, password, executeBeforeTFA, remember, executeAfterTermsUseAccepted}: { email: string, password: string, executeBeforeTFA?: Function, remember: boolean, executeAfterTermsUseAccepted?: Function}) => {
		const response = await RCCServicesApi.postRequest({
			method: "Auth/LogIn", 
			body: { Username: email, Password: password }
		});
		
		const { token, eftba005, eftbb001,  eftba001, eftba002, administrator, superUser, userConfig, hasTFAAuthentication, scheduleRestriction, systemNotifications, hasUseTermNotAccepted, isMaintenance, tokenExpirationHours, needToChangePassword } = response.data;
		setCookie("RememberMe", remember ? "true" : "false", { secure: true, expires: tokenExpirationHours / 24 });
		rememberMe = !!remember;
		updateMaintenance(isMaintenance);

		if(hasTFAAuthentication){
			if(executeBeforeTFA)
				executeBeforeTFA();
			modalTFA.current?.openTFAModal(eftba005.efa005Id, () => executeOnSuccessfulLogin({ token, eftba005, eftbb001, eftba001, eftba002, administrator, superUser, userConfig, scheduleRestriction, systemNotifications, hasUseTermNotAccepted, tokenExpirationHours, needToChangePassword, executeAfterTermsUseAccepted }));
			return;
		}

		executeOnSuccessfulLogin({ token, eftba005, eftbb001, eftba001, eftba002, administrator, superUser, userConfig, scheduleRestriction, systemNotifications, hasUseTermNotAccepted, tokenExpirationHours, needToChangePassword, executeAfterTermsUseAccepted });
	};

	const removeCookiesUserConfig = () => {
		removeCookieFunction(ScreenStandardGroup.ACTION);
		removeCookieFunction(ScreenStandardGroup.AUDITS);
		removeCookieFunction(ScreenStandardGroup.INDICATORS);
		removeCookieFunction(ScreenStandardGroup.MEETINGS);
		removeCookieFunction(ScreenStandardGroup.OCCURRENCES);		
		removeCookieFunction(ScreenStandardGroup.OKR);			
		removeCookieFunction(ScreenStandardGroup.RISKS);
		removeCookieFunction("AppDefaultDashboard");
	};

	const signOut = () => {
		signOutFunction();
	};

	const updateCompanyLogged = (eftba002: ICompany, eftba001: IBusinessGroup, token: string, eftbb001: IPermissions[], userConfig: IUserConfig, administrator: boolean, scheduleRestriction: boolean, tokenExpirationHours: number) => {
		removeCookieFunction("Token");
		removeCookieFunction("BusinessGroup");
		removeCookieFunction("Company");
		removeCookieFunction("Administrator");
		removeCookiesUserConfig();
		removeLocalStorage("PermissionsMenu");
		removeCookieFunction("ScheduleRestriction");

		const expires = tokenExpirationHours / 24;

		setCookieFunction("Token", token, { secure: true, expires });
		setCookieFunction("BusinessGroup", JSON.stringify(eftba001), { secure: true, expires });
		setCookieFunction("Company", JSON.stringify(eftba002), { secure: true, expires });
		setCookieFunction("Administrator", String(administrator), { secure: true, expires });
		updateUserConfig(userConfig);
		setLocalStorage("PermissionsMenu", JSON.stringify(eftbb001));
		setCookieFunction("ScheduleRestriction", String(scheduleRestriction),  { secure: true, expires });
		setScheduleRestrict(String(scheduleRestriction) === "true");
		
		RCCServicesApi.addHeader("authorization", `Bearer ${token}`);

		setCredentials({
			...credentials,
			token,
			eftba001,
			eftba002
		});
	};

	const updateUserImage = (efa005Imagem: string) => {
		removeCookieFunction("User");  
		
		const newEftba005 = {
			...credentials.eftba005,
			efa005Imagem
		};

		setCookieFunction("User", JSON.stringify(newEftba005), { secure: true, expires: 1 });
		setCredentials({
			...credentials,
			eftba005: newEftba005
		});
	};

	const updateUserNameAndTreatment = (efa005Nome: string, efa005Tratamento?: string) => {
		removeCookieFunction("User");  
		
		const newEftba005 = efa005Tratamento ? {
			...credentials.eftba005,
			efa005Nome,
			efa005Tratamento
		} : {
			...credentials.eftba005,
			efa005Nome
		};

		setCookieFunction("User", JSON.stringify(newEftba005), { secure: true, expires: 1 });
		setCredentials({
			...credentials,
			eftba005: newEftba005
		});
	};

	const updateCompanyImage = (efa002Imagem: string) => {
		removeCookieFunction("Company");  
		
		const newEftba002 = {
			...credentials.eftba002,
			efa002Imagem
		};

		setCookieFunction("Company", JSON.stringify(newEftba002), { secure: true, expires: 1 });
		setCredentials({
			...credentials,
			eftba002: newEftba002
		});
	};

	const updateCompanyName = (efa002Fantasia: string) => {
		removeCookieFunction("Company");  
		
		const newEftba002 = {
			...credentials.eftba002,
			efa002Fantasia
		};

		setCookieFunction("Company", JSON.stringify(newEftba002), { secure: true, expires: 1 });
		setCredentials({
			...credentials,
			eftba002: newEftba002
		});
	};


	const updateHomePage = (page: string) => {
		removeCookieFunction("HomePage");
		setHomePage(page);
		setCookieFunction("HomePage", page);
	};

	const updateDefaultDashboard = (dashboardId: string | null) => {
		removeCookieFunction("AppDefaultDashboard");
		if(dashboardId !== null)
			setCookieFunction("AppDefaultDashboard", dashboardId);
		setDefaultDashboard(dashboardId);
	};

	const checkIfHasPermission = (path: string) => hasPermissions(permissions, path, credentials.administrator, credentials.superUser);
	const checkIfHasSpecificPermission = (path: string, permission: PermissionEnum) => hasSpecificPermissions(permissions, path, credentials.administrator || credentials.superUser, permission);
	const checkIfHasContractPermission = (path: string) => hasContractPermissions(permissions, path);

	useEffect(() => {
		const needToChangePassword = getCookieFunction("NeedToChangePassword");
		if(needToChangePassword && needToChangePassword === "true")
			modalsProfileRef.current?.openModalChangePassword(true);
	},[]);

	return (
		<AuthContext.Provider value={{
			administrator: credentials.administrator,
			superUser: credentials.superUser,
			user: credentials.eftba005,
			permissions,
			businessGroup: credentials.eftba001,
			company: credentials.eftba002,
			defaultDashboard,
			homePage,
			scheduleRestriction: scheduleRestrict,
			systemNotifications: notifications,
			signIn,
			signOut,
			updateCompanyLogged,
			updateUserImage,
			updateCompanyName,
			updateCompanyImage,
			updateUserNameAndTreatment,
			updateDefaultDashboard,
			updateHomePage,
			checkIfHasPermission,
			checkIfHasSpecificPermission,
			checkIfHasContractPermission
		}}>
			{children}
			<ModalTFA ref={modalTFA}/>
			<ModalTermsUse ref={modalTermsUseRef} />
			<ModalsProfile ref={modalsProfileRef}/> 
		</AuthContext.Provider>
	);
};

export default AuthProvider;
